import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { 
  Settings, 
  Building2,
  Mail,
  Phone,
  MapPin,
  Globe,
  Save,
  Bell,
  Loader2,
  Trash2,
  Database,
  AlertTriangle,
  CheckCircle2,
  RefreshCw,
  Plus,
  Send,
  CreditCard,
  RotateCcw
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/axios';

const AdminSettingsPage = () => {
  const { theme } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [settings, setSettings] = useState(null);
  const [smtpSettings, setSmtpSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('agency');
  
  // Test data state
  const [testDataStatus, setTestDataStatus] = useState(null);
  const [loadingTestData, setLoadingTestData] = useState(false);
  const [deletingTestData, setDeletingTestData] = useState(false);
  const [generatingTestData, setGeneratingTestData] = useState(false);
  
  // Full purge state
  const [dataStats, setDataStats] = useState(null);
  const [showPurgeConfirm, setShowPurgeConfirm] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [purging, setPurging] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [keepAdmin, setKeepAdmin] = useState(true);
  const [keepTeam, setKeepTeam] = useState(true);
  const [keepTemplates, setKeepTemplates] = useState(true);
  
  // SMTP test state
  const [testEmail, setTestEmail] = useState('');
  const [sendingTestEmail, setSendingTestEmail] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const [agencyRes, smtpRes, testDataRes, dataStatsRes] = await Promise.all([
        api.get('/settings/agency-info').catch(() => ({ data: null })),
        api.get('/settings/smtp').catch(() => ({ data: null })),
        api.get('/settings/test-data/status').catch(() => ({ data: null })),
        api.get('/settings/data/stats').catch(() => ({ data: null }))
      ]);
      
      setSettings(agencyRes.data || {
        name: 'MSC L\'AGENCE',
        email: 'contact@msc.fr',
        phone: '',
        address: '',
        postal_code: '',
        city: '',
        country: 'France',
        website: '',
        siret: '',
        tva_number: '',
        custom_domain: '',
        legal_status: '',
        capital: '',
        representative: '',
        representative_title: '',
      });
      
      setSmtpSettings(smtpRes.data || {
        smtp_host: '',
        smtp_port: 465,
        smtp_user: '',
        smtp_password: '',
        sender_email: '',
        sender_name: '',
        use_ssl: true,
        use_tls: false,
        enabled: false
      });
      
      setTestDataStatus(testDataRes.data || { has_test_data: false, counts: {}, total: 0 });
      setDataStats(dataStatsRes.data || null);
    } catch (error) {
      console.error('Error loading settings:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleClearTestData = async (type = 'all') => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer les données de test ?')) return;
    
    setDeletingTestData(true);
    try {
      const params = type === 'all' 
        ? { clear_domains: true, clear_projects: true, clear_tasks: true, clear_tickets: true, clear_invoices: true, clear_contracts: true }
        : { [`clear_${type}`]: true };
      
      const response = await api.delete('/settings/test-data/clear', { params });
      toast.success(response.data.message);
      
      // Refresh test data status
      const statusRes = await api.get('/settings/test-data/status');
      setTestDataStatus(statusRes.data);
    } catch (error) {
      console.error('Error clearing test data:', error);
      toast.error('Erreur lors de la suppression');
    } finally {
      setDeletingTestData(false);
    }
  };

  const handleGenerateTestDomains = async () => {
    setGeneratingTestData(true);
    try {
      const response = await api.post('/settings/test-data/generate-domains');
      toast.success(response.data.message);
      
      // Refresh test data status
      const statusRes = await api.get('/settings/test-data/status');
      setTestDataStatus(statusRes.data);
    } catch (error) {
      console.error('Error generating test data:', error);
      toast.error('Erreur lors de la génération');
    } finally {
      setGeneratingTestData(false);
    }
  };

  const handlePurgeAllData = async () => {
    setPurging(true);
    try {
      const response = await api.post('/settings/data/purge-all', {
        confirm: true,
        keep_admin: keepAdmin,
        keep_team: keepTeam,
        keep_templates: keepTemplates
      });
      toast.success(response.data.message);
      setShowPurgeConfirm(false);
      
      // Refresh all stats
      const [testDataRes, dataStatsRes] = await Promise.all([
        api.get('/settings/test-data/status').catch(() => ({ data: null })),
        api.get('/settings/data/stats').catch(() => ({ data: null }))
      ]);
      setTestDataStatus(testDataRes.data);
      setDataStats(dataStatsRes.data);
    } catch (error) {
      console.error('Error purging data:', error);
      toast.error('Erreur lors de la purge');
    } finally {
      setPurging(false);
    }
  };

  const handleResetToZero = async () => {
    setResetting(true);
    try {
      const response = await api.post('/settings/data/reset-to-zero');
      toast.success(response.data.message);
      setShowResetConfirm(false);
      
      // Refresh all stats
      const [testDataRes, dataStatsRes] = await Promise.all([
        api.get('/settings/test-data/status').catch(() => ({ data: null })),
        api.get('/settings/data/stats').catch(() => ({ data: null }))
      ]);
      setTestDataStatus(testDataRes.data);
      setDataStats(dataStatsRes.data);
    } catch (error) {
      console.error('Error resetting data:', error);
      toast.error('Erreur lors de la remise à zéro');
    } finally {
      setResetting(false);
    }
  };

  const handleSaveAgency = async () => {
    setSaving(true);
    try {
      await api.put('/settings/agency-info', settings);
      toast.success('Informations sauvegardées');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSmtp = async () => {
    setSaving(true);
    try {
      await api.put('/settings/smtp', smtpSettings);
      toast.success('Configuration SMTP sauvegardée');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleTestSmtp = async () => {
    if (!testEmail) {
      toast.error('Veuillez saisir une adresse email de test');
      return;
    }
    
    setSendingTestEmail(true);
    try {
      await api.post('/settings/smtp/test', { test_email: testEmail });
      toast.success(`Email de test envoyé à ${testEmail} !`);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Erreur lors de l\'envoi du test';
      toast.error(errorMsg);
    } finally {
      setSendingTestEmail(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8fafc]">
        <Sidebar 
          collapsed={collapsed} 
          setCollapsed={setCollapsed}
          mobileOpen={mobileOpen}
          setMobileOpen={setMobileOpen}
        />
        <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
        <main className={cn(
          "transition-all duration-300 px-4 md:px-6 pb-6",
          collapsed ? "md:ml-20" : "md:ml-64"
        )} style={{ paddingTop: "130px" }}>
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar 
        collapsed={collapsed} 
        setCollapsed={setCollapsed}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
      />
      <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
      <main className={cn(
        "transition-all duration-300 px-4 md:px-6 pb-6",
        collapsed ? "md:ml-20" : "md:ml-64"
      )} style={{ paddingTop: "130px" }}>
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold" style={{ color: theme.colors?.primary }}>Paramètres</h1>
              <p className="text-[#64748b]">Configuration générale de votre agence</p>
            </div>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList>
              <TabsTrigger value="agency"><Building2 className="w-4 h-4 mr-2" />Agence</TabsTrigger>
              <TabsTrigger value="email"><Mail className="w-4 h-4 mr-2" />Email (SMTP)</TabsTrigger>
              <TabsTrigger value="testdata"><Database className="w-4 h-4 mr-2" />Données de test</TabsTrigger>
            </TabsList>

            <TabsContent value="agency" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Informations de l'agence</CardTitle>
                  <CardDescription>Ces informations apparaissent sur votre site vitrine et vos documents</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Nom de l'agence</Label>
                      <Input 
                        value={settings?.name || ''}
                        onChange={(e) => setSettings({...settings, name: e.target.value})}
                    placeholder="MSC L'AGENCE"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Site web</Label>
                  <Input 
                    value={settings?.website || ''}
                    onChange={(e) => setSettings({...settings, website: e.target.value})}
                    placeholder="https://msc.fr"
                  />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Email de contact</Label>
                  <Input 
                    type="email"
                    value={settings?.email || ''}
                    onChange={(e) => setSettings({...settings, email: e.target.value})}
                    placeholder="contact@msc.fr"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Téléphone</Label>
                  <Input 
                    value={settings?.phone || ''}
                    onChange={(e) => setSettings({...settings, phone: e.target.value})}
                    placeholder="01 23 45 67 89"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Adresse</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Adresse</Label>
                <Input 
                  value={settings?.address || ''}
                  onChange={(e) => setSettings({...settings, address: e.target.value})}
                  placeholder="123 Rue Exemple"
                />
              </div>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Code postal</Label>
                  <Input 
                    value={settings?.postal_code || ''}
                    onChange={(e) => setSettings({...settings, postal_code: e.target.value})}
                    placeholder="75001"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Ville</Label>
                  <Input 
                    value={settings?.city || ''}
                    onChange={(e) => setSettings({...settings, city: e.target.value})}
                    placeholder="Paris"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Pays</Label>
                  <Input 
                    value={settings?.country || ''}
                    onChange={(e) => setSettings({...settings, country: e.target.value})}
                    placeholder="France"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Informations légales</CardTitle>
            </CardHeader>
            <CardContent className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>SIRET</Label>
                <Input 
                  value={settings?.siret || ''}
                  onChange={(e) => setSettings({...settings, siret: e.target.value})}
                  placeholder="123 456 789 00012"
                />
              </div>
              <div className="space-y-2">
                <Label>N° TVA Intracommunautaire</Label>
                <Input 
                  value={settings?.tva_number || ''}
                  onChange={(e) => setSettings({...settings, tva_number: e.target.value})}
                  placeholder="FR12345678901"
                />
              </div>
              <div className="space-y-2">
                <Label>Forme juridique</Label>
                <Input 
                  value={settings?.legal_status || ''}
                  onChange={(e) => setSettings({...settings, legal_status: e.target.value})}
                  placeholder="SARL, SAS, Auto-entrepreneur..."
                />
              </div>
              <div className="space-y-2">
                <Label>Capital social</Label>
                <Input 
                  value={settings?.capital || ''}
                  onChange={(e) => setSettings({...settings, capital: e.target.value})}
                  placeholder="10 000 €"
                />
              </div>
              <div className="space-y-2">
                <Label>Représentant légal</Label>
                <Input 
                  value={settings?.representative || ''}
                  onChange={(e) => setSettings({...settings, representative: e.target.value})}
                  placeholder="Nom du gérant"
                />
              </div>
              <div className="space-y-2">
                <Label>Fonction du représentant</Label>
                <Input 
                  value={settings?.representative_title || ''}
                  onChange={(e) => setSettings({...settings, representative_title: e.target.value})}
                  placeholder="Gérant, Président..."
                />
              </div>
            </CardContent>
          </Card>

          {/* Bank Details Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="w-5 h-5" />
                Coordonnées bancaires (RIB)
              </CardTitle>
              <CardDescription>
                Ces informations seront affichées aux clients lors d'un paiement par virement bancaire
              </CardDescription>
            </CardHeader>
            <CardContent className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nom de la banque</Label>
                <Input 
                  value={settings?.bank_name || ''}
                  onChange={(e) => setSettings({...settings, bank_name: e.target.value})}
                  placeholder="Crédit Agricole, BNP Paribas..."
                />
              </div>
              <div className="space-y-2">
                <Label>Titulaire du compte</Label>
                <Input 
                  value={settings?.bank_account_holder || ''}
                  onChange={(e) => setSettings({...settings, bank_account_holder: e.target.value})}
                  placeholder="MSC L'AGENCE"
                />
              </div>
              <div className="space-y-2">
                <Label>IBAN</Label>
                <Input 
                  value={settings?.bank_iban || ''}
                  onChange={(e) => setSettings({...settings, bank_iban: e.target.value})}
                  placeholder="FR76 1234 5678 9012 3456 7890 123"
                />
              </div>
              <div className="space-y-2">
                <Label>BIC / SWIFT</Label>
                <Input 
                  value={settings?.bank_bic || ''}
                  onChange={(e) => setSettings({...settings, bank_bic: e.target.value})}
                  placeholder="BNPAFRPP"
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="w-5 h-5" />
                Domaine personnalisé
              </CardTitle>
              <CardDescription>
                URL de votre domaine personnalisé pour les emails et liens (ex: https://manager.agence-msc.fr)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Label>URL du domaine personnalisé</Label>
                <Input 
                  value={settings?.custom_domain || ''}
                  onChange={(e) => setSettings({...settings, custom_domain: e.target.value})}
                  placeholder="https://manager.agence-msc.fr"
                />
                <p className="text-xs text-slate-500">
                  Cette URL sera utilisée dans les emails envoyés aux clients (création de compte, notifications, etc.)
                </p>
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end">
            <Button onClick={handleSaveAgency} disabled={saving} className="bg-amber-600 hover:bg-amber-700">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Sauvegarder
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="email" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Configuration SMTP</CardTitle>
              <CardDescription>Paramètres pour l'envoi d'emails (notifications, factures, etc.)</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Serveur SMTP</Label>
                  <Input 
                    value={smtpSettings?.smtp_host || ''}
                    onChange={(e) => setSmtpSettings({...smtpSettings, smtp_host: e.target.value})}
                    placeholder="mail.planethoster.net"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Port</Label>
                  <Input 
                    type="number"
                    value={smtpSettings?.smtp_port || 465}
                    onChange={(e) => setSmtpSettings({...smtpSettings, smtp_port: parseInt(e.target.value) || 465})}
                  />
                  <p className="text-xs text-slate-500">465 pour SSL, 587 pour TLS</p>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Utilisateur</Label>
                  <Input 
                    value={smtpSettings?.smtp_user || ''}
                    onChange={(e) => setSmtpSettings({...smtpSettings, smtp_user: e.target.value})}
                    placeholder="user@exemple.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Mot de passe</Label>
                  <Input 
                    type="password"
                    value={smtpSettings?.smtp_password || ''}
                    onChange={(e) => setSmtpSettings({...smtpSettings, smtp_password: e.target.value})}
                    placeholder="••••••••"
                  />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Email expéditeur</Label>
                  <Input 
                    type="email"
                    value={smtpSettings?.sender_email || ''}
                    onChange={(e) => setSmtpSettings({...smtpSettings, sender_email: e.target.value})}
                    placeholder="noreply@agence-msc.fr"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Nom expéditeur</Label>
                  <Input 
                    value={smtpSettings?.sender_name || ''}
                    onChange={(e) => setSmtpSettings({...smtpSettings, sender_name: e.target.value})}
                    placeholder="MSC Manager"
                  />
                </div>
              </div>
              
              {/* SSL/TLS Options */}
              <div className="p-4 bg-slate-50 rounded-lg space-y-3">
                <p className="text-sm font-medium text-slate-700">Sécurité de la connexion</p>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Switch 
                      id="use_ssl"
                      checked={smtpSettings?.use_ssl || false}
                      onCheckedChange={(checked) => setSmtpSettings({...smtpSettings, use_ssl: checked, use_tls: checked ? false : smtpSettings?.use_tls})}
                    />
                    <Label htmlFor="use_ssl" className="cursor-pointer">SSL (port 465)</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch 
                      id="use_tls"
                      checked={smtpSettings?.use_tls || false}
                      onCheckedChange={(checked) => setSmtpSettings({...smtpSettings, use_tls: checked, use_ssl: checked ? false : smtpSettings?.use_ssl})}
                    />
                    <Label htmlFor="use_tls" className="cursor-pointer">STARTTLS (port 587)</Label>
                  </div>
                </div>
                <p className="text-xs text-slate-500">
                  PlanetHoster : utilisez SSL avec le port 465
                </p>
              </div>
              
              <div className="flex items-center gap-2 pt-2">
                <Switch 
                  id="smtp_enabled"
                  checked={smtpSettings?.enabled || false}
                  onCheckedChange={(checked) => setSmtpSettings({...smtpSettings, enabled: checked})}
                />
                <Label htmlFor="smtp_enabled" className="cursor-pointer">Activer l'envoi d'emails</Label>
              </div>
            </CardContent>
          </Card>

          {/* Test Email Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Send className="w-5 h-5" />
                Tester l'envoi d'email
              </CardTitle>
              <CardDescription>
                Envoyez un email de test pour vérifier que la configuration SMTP fonctionne
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex gap-3">
                <Input 
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="votre@email.com"
                  className="flex-1"
                />
                <Button 
                  variant="outline" 
                  onClick={handleTestSmtp}
                  disabled={sendingTestEmail || !testEmail}
                >
                  {sendingTestEmail ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  Envoyer un test
                </Button>
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end gap-2">
            <Button onClick={handleSaveSmtp} disabled={saving} className="bg-amber-600 hover:bg-amber-700">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Sauvegarder
            </Button>
          </div>
        </TabsContent>

        {/* Test Data Tab */}
        <TabsContent value="testdata" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="w-5 h-5" />
                Gestion des Données de Test
              </CardTitle>
              <CardDescription>
                Gérez les données de démonstration présentes dans votre plateforme
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Status */}
              <div className="p-4 rounded-lg border bg-slate-50">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold flex items-center gap-2">
                    {testDataStatus?.has_test_data ? (
                      <>
                        <AlertTriangle className="w-4 h-4 text-amber-500" />
                        Données de test présentes
                      </>
                    ) : (
                      <>
                        <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                        Aucune donnée de test
                      </>
                    )}
                  </h3>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={loadSettings}
                  >
                    <RefreshCw className="w-3 h-3 mr-1" />
                    Actualiser
                  </Button>
                </div>
                
                {testDataStatus?.has_test_data && (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                    <div className="text-center p-3 bg-white rounded-lg border">
                      <div className="text-2xl font-bold text-slate-700">{testDataStatus?.counts?.domains || 0}</div>
                      <div className="text-xs text-slate-500">Domaines</div>
                    </div>
                    <div className="text-center p-3 bg-white rounded-lg border">
                      <div className="text-2xl font-bold text-slate-700">{testDataStatus?.counts?.projects || 0}</div>
                      <div className="text-xs text-slate-500">Projets</div>
                    </div>
                    <div className="text-center p-3 bg-white rounded-lg border">
                      <div className="text-2xl font-bold text-slate-700">{testDataStatus?.counts?.tasks || 0}</div>
                      <div className="text-xs text-slate-500">Tâches</div>
                    </div>
                    <div className="text-center p-3 bg-white rounded-lg border">
                      <div className="text-2xl font-bold text-slate-700">{testDataStatus?.counts?.tickets || 0}</div>
                      <div className="text-xs text-slate-500">Tickets</div>
                    </div>
                    <div className="text-center p-3 bg-white rounded-lg border">
                      <div className="text-2xl font-bold text-slate-700">{testDataStatus?.counts?.invoices || 0}</div>
                      <div className="text-xs text-slate-500">Factures</div>
                    </div>
                    <div className="text-center p-3 bg-white rounded-lg border">
                      <div className="text-2xl font-bold text-slate-700">{testDataStatus?.counts?.contracts || 0}</div>
                      <div className="text-xs text-slate-500">Contrats</div>
                    </div>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="space-y-4">
                <h3 className="font-semibold text-slate-700">Actions</h3>
                
                {/* Generate Test Data */}
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <p className="font-medium">Générer des domaines de test</p>
                    <p className="text-sm text-slate-500">Crée 3 domaines de démonstration (actif, expire bientôt, expiré)</p>
                  </div>
                  <Button 
                    variant="outline"
                    onClick={handleGenerateTestDomains}
                    disabled={generatingTestData}
                  >
                    {generatingTestData ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Plus className="w-4 h-4 mr-2" />
                    )}
                    Générer
                  </Button>
                </div>

                {/* Clear Test Data */}
                {testDataStatus?.has_test_data && (
                  <>
                    <div className="flex items-center justify-between p-4 border border-red-200 rounded-lg bg-red-50">
                      <div>
                        <p className="font-medium text-red-700">Supprimer les domaines de test</p>
                        <p className="text-sm text-red-600">Supprime uniquement les domaines marqués comme données de test</p>
                      </div>
                      <Button 
                        variant="outline"
                        className="border-red-300 text-red-600 hover:bg-red-100"
                        onClick={() => handleClearTestData('domains')}
                        disabled={deletingTestData}
                      >
                        {deletingTestData ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4 mr-2" />
                        )}
                        Supprimer
                      </Button>
                    </div>

                    <div className="flex items-center justify-between p-4 border border-red-300 rounded-lg bg-red-100">
                      <div>
                        <p className="font-medium text-red-800">Supprimer TOUTES les données de test</p>
                        <p className="text-sm text-red-700">
                          Supprime {testDataStatus?.total || 0} éléments de test (domaines, projets, tâches, tickets, factures, contrats)
                        </p>
                      </div>
                      <Button 
                        variant="destructive"
                        onClick={() => handleClearTestData('all')}
                        disabled={deletingTestData}
                      >
                        {deletingTestData ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4 mr-2" />
                        )}
                        Tout supprimer
                      </Button>
                    </div>
                  </>
                )}
              </div>

              {/* Info */}
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm text-blue-800">
                  <strong>Information :</strong> Les données de test sont marquées avec un indicateur spécial 
                  (<code className="bg-blue-100 px-1 rounded">is_test_data: true</code>). 
                  Seules ces données seront supprimées. Vos données réelles ne seront jamais affectées.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Full Data Purge Section */}
          <Card className="border-red-300">
            <CardHeader className="bg-red-50">
              <CardTitle className="flex items-center gap-2 text-red-700">
                <AlertTriangle className="w-5 h-5" />
                Gestion des données
              </CardTitle>
              <CardDescription className="text-red-600">
                Repartir de zéro ou supprimer toutes les données de l'application
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6 pt-6">
              {/* Current Data Stats */}
              {dataStats && (
                <div className="space-y-4">
                  <h3 className="font-semibold text-slate-700">Données actuelles dans la base</h3>
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-lg font-bold text-slate-700">
                        Total: {dataStats.total_records || 0} enregistrements
                      </span>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => loadSettings()}
                      >
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Actualiser
                      </Button>
                    </div>
                    
                    {/* Users by role */}
                    {dataStats.users_by_role && Object.keys(dataStats.users_by_role).length > 0 && (
                      <div className="mb-4">
                        <p className="text-sm font-medium text-slate-600 mb-2">Utilisateurs par rôle:</p>
                        <div className="flex flex-wrap gap-2">
                          {Object.entries(dataStats.users_by_role).map(([role, count]) => (
                            <Badge key={role} variant="secondary">
                              {role}: {count}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Collections breakdown */}
                    {dataStats.collections && Object.keys(dataStats.collections).length > 0 && (
                      <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
                        {Object.entries(dataStats.collections).map(([collection, count]) => (
                          <div key={collection} className="text-center p-2 bg-white rounded border">
                            <div className="font-bold text-slate-700">{count}</div>
                            <div className="text-xs text-slate-500 truncate">{collection}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Reset to Zero Button - Recommended */}
              {!showResetConfirm ? (
                <div className="flex items-center justify-between p-4 border-2 border-amber-300 rounded-lg bg-amber-50">
                  <div>
                    <p className="font-medium text-amber-800 flex items-center gap-2">
                      <RotateCcw className="w-4 h-4" />
                      Repartir de zéro (recommandé)
                    </p>
                    <p className="text-sm text-amber-700">
                      Supprime projets, tickets, tâches, contrats clients. <strong>Conserve</strong> l'admin, l'équipe et les modèles.
                    </p>
                  </div>
                  <Button 
                    variant="outline"
                    className="border-amber-400 text-amber-700 hover:bg-amber-100"
                    onClick={() => setShowResetConfirm(true)}
                  >
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Repartir de zéro
                  </Button>
                </div>
              ) : (
                <div className="p-6 border-2 border-amber-500 rounded-lg bg-amber-100 space-y-4">
                  <div className="flex items-center gap-3 text-amber-800">
                    <RotateCcw className="w-8 h-8" />
                    <div>
                      <p className="font-bold text-lg">Confirmation - Repartir de zéro</p>
                      <p className="text-sm">Cette action supprimera tous les projets, tickets et données clients.</p>
                    </div>
                  </div>
                  
                  <div className="p-3 bg-white rounded-lg space-y-2">
                    <p className="text-sm font-medium text-emerald-700">✅ Éléments conservés :</p>
                    <ul className="text-sm text-slate-600 list-disc list-inside">
                      <li>Votre compte administrateur</li>
                      <li>Tous les membres de l'équipe</li>
                      <li>Les modèles de briefs et contrats</li>
                      <li>Les paramètres système</li>
                    </ul>
                  </div>
                  
                  <div className="flex gap-3">
                    <Button 
                      variant="outline" 
                      onClick={() => setShowResetConfirm(false)}
                      className="flex-1"
                    >
                      Annuler
                    </Button>
                    <Button 
                      className="flex-1 bg-amber-600 hover:bg-amber-700"
                      onClick={handleResetToZero}
                      disabled={resetting}
                    >
                      {resetting ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <RotateCcw className="w-4 h-4 mr-2" />
                      )}
                      Confirmer
                    </Button>
                  </div>
                </div>
              )}

              {/* Full Purge Button */}
              {!showPurgeConfirm ? (
                <div className="flex items-center justify-between p-4 border-2 border-red-300 rounded-lg bg-red-50">
                  <div>
                    <p className="font-medium text-red-800">⚠️ Supprimer TOUTES les données</p>
                    <p className="text-sm text-red-600">
                      Action IRRÉVERSIBLE. Tous les projets, clients, membres, tickets, etc. seront supprimés.
                    </p>
                  </div>
                  <Button 
                    variant="destructive"
                    onClick={() => setShowPurgeConfirm(true)}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Purge totale
                  </Button>
                </div>
              ) : (
                <div className="p-6 border-2 border-red-500 rounded-lg bg-red-100 space-y-4">
                  <div className="flex items-center gap-3 text-red-800">
                    <AlertTriangle className="w-8 h-8" />
                    <div>
                      <p className="font-bold text-lg">⚠️ CONFIRMATION REQUISE</p>
                      <p className="text-sm">Êtes-vous sûr de vouloir supprimer TOUTES les données ?</p>
                    </div>
                  </div>
                  
                  <div className="space-y-3 p-3 bg-white rounded-lg">
                    <div className="flex items-center gap-3">
                      <Switch 
                        id="keep-admin" 
                        checked={keepAdmin} 
                        onCheckedChange={setKeepAdmin}
                      />
                      <Label htmlFor="keep-admin" className="cursor-pointer">
                        Conserver mon compte administrateur
                      </Label>
                    </div>
                    <div className="flex items-center gap-3">
                      <Switch 
                        id="keep-team" 
                        checked={keepTeam} 
                        onCheckedChange={setKeepTeam}
                      />
                      <Label htmlFor="keep-team" className="cursor-pointer">
                        Conserver les membres de l'équipe
                      </Label>
                    </div>
                    <div className="flex items-center gap-3">
                      <Switch 
                        id="keep-templates" 
                        checked={keepTemplates} 
                        onCheckedChange={setKeepTemplates}
                      />
                      <Label htmlFor="keep-templates" className="cursor-pointer">
                        Conserver les modèles (briefs, contrats)
                      </Label>
                    </div>
                  </div>
                  
                  <div className="flex gap-3">
                    <Button 
                      variant="outline" 
                      onClick={() => setShowPurgeConfirm(false)}
                      className="flex-1"
                    >
                      Annuler
                    </Button>
                    <Button 
                      variant="destructive"
                      onClick={handlePurgeAllData}
                      disabled={purging}
                      className="flex-1"
                    >
                      {purging ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4 mr-2" />
                      )}
                      Confirmer la purge
                    </Button>
                  </div>
                </div>
              )}

              {/* Warning */}
              <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-sm text-amber-800">
                  <strong>💡 Conseil :</strong> Utilisez "Repartir de zéro" si vous souhaitez simplement 
                  supprimer les données clients et projets tout en conservant votre équipe et vos modèles.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

        <Footer />
        </div>
      </main>
    </div>
  );
};

export default AdminSettingsPage;
